import { Button } from '@/components/ui/button.tsx';
import EmojiSelector from '@/components/ui/emojis/emoji-selector.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { useEmojiReactions } from '@/hooks/use-emoji-reactions.ts';
import { cn, pluralize } from '@/lib/utils.ts';
import type {
  EmojiReactionSummary,
  EmojiReactionType,
} from '@/models/types.ts';
import { observer } from 'mobx-react-lite';

const EmojiReactions = observer(
  ({
    targetId,
    targetType,
    className,
    reactionButtonClassName,
    selectorButtonClassName,
  }: {
    targetId: string;
    targetType: string;
    className?: string;
    reactionButtonClassName?: string;
    selectorButtonClassName?: string;
  }) => {
    const {
      summary: reactions,
      toggle,
      user,
    } = useEmojiReactions(targetId, targetType as EmojiReactionType);

    const getTooltipContent = (reaction: EmojiReactionSummary): string => {
      if (!reaction.usersReacted || reaction.usersReacted.length === 0) {
        return 'No reactions';
      }

      const formatUserList = (users: string[]): string => {
        if (users.length === 1) {
          return users[0];
        }
        if (users.length === 2) {
          return `${users[0]} and ${users[1]}`;
        }

        const allButLast = users.slice(0, -1);
        const last = users[users.length - 1];
        return `${allButLast.join(', ')}, and ${last}`;
      };

      const otherUsers = reaction.usersReacted.filter(
        (username) => username !== user.username,
      );

      if (reaction.usersReacted.length === 1) {
        return reaction.hasUserReacted
          ? `You reacted with ${reaction.emoji}`
          : `${otherUsers[0]} reacted with ${reaction.emoji}`;
      }

      if (reaction.hasUserReacted && otherUsers.length === 1) {
        return `You and ${otherUsers[0]} reacted with ${reaction.emoji}`;
      }

      if (reaction.hasUserReacted && otherUsers.length === 2) {
        return `You, ${otherUsers[0]}, and ${otherUsers[1]} reacted with ${reaction.emoji}`;
      }

      if (reaction.hasUserReacted && otherUsers.length > 2) {
        const remaining = otherUsers.length - 2;
        return `You, ${otherUsers[0]}, ${otherUsers[1]}, and ${remaining} ${pluralize(remaining, 'other', 'others')} reacted with ${reaction.emoji}`;
      }

      if (otherUsers.length <= 3) {
        return `${formatUserList(otherUsers)} reacted with ${reaction.emoji}`;
      }

      const firstThreeUsers = otherUsers.slice(0, 3);
      const remaining = otherUsers.length - 3;
      const remainingText = `${remaining} ${pluralize(remaining, 'other', 'others')}`;
      return `${formatUserList([...firstThreeUsers, remainingText])} reacted with ${reaction.emoji}`;
    };

    return (
      <div className={cn('flex items-center gap-2 flex-wrap', className)}>
        {Object.entries(reactions).map(
          ([emojiKey, reaction]: [string, EmojiReactionSummary]) => (
            <Tooltip key={emojiKey}>
              <TooltipTrigger asChild>
                <Button
                  key={emojiKey}
                  variant={reaction.hasUserReacted ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-7 px-2 rounded-full text-sm leading-none',
                    reaction.hasUserReacted
                      ? 'bg-gray-200 border text-gray-700 hover:bg-gray-100'
                      : 'border text-gray-700 hover:bg-gray-100',
                    reactionButtonClassName,
                  )}
                  onClick={() => toggle(reaction.emoji)}
                >
                  <span className="mr-1">{reaction.emoji}</span>
                  <span>{reaction.usersReacted.length}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{getTooltipContent(reaction)}</TooltipContent>
            </Tooltip>
          ),
        )}
        <EmojiSelector
          targetId={targetId}
          targetType={targetType as EmojiReactionType}
          buttonClassName={selectorButtonClassName}
        />
      </div>
    );
  },
);

export default EmojiReactions;
